/**
 * Technician Routes API Service
 *
 * Provides CRUD operations for technician routes stored in prod.technician_routes.
 * Uses SECURITY DEFINER RPC functions for all database operations.
 *
 * IMPORTANT: RPC functions get tenant_id from JWT claims internally via get_jwt_tenant_id().
 * Do NOT pass p_tenant_id to RPC calls - the function will get it from JWT automatically.
 */

import { supabase, getProdSupabaseClient } from './client';
import { logSystemError } from '@/lib/utils/errorTranslator';
import {
  calculateDistance,
  DUPLICATE_LOCATION_THRESHOLD_MILES
} from '@/features/routing/utils/routeOptimization';
import { optimizeRouteWithMapbox } from '@/features/routing/utils/mapboxOptimization';
import type {
  TechnicianRouteRecord,
  TechnicianRouteWithDetails,
  RouteData,
  RouteStatus,
  RouteStop,
  RouteRecall,
  TenantRouteSettings,
  ReleasedRecall
} from '@/types/technicianRoutes';

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the main Supabase client for RPC calls
 * RPC functions are in the public schema and PostgREST only exposes public schema functions
 * The public wrappers internally call the prod schema functions
 */
const getRpcClient = () => {
  return supabase;
};

/**
 * Get the prod Supabase client for direct table queries
 */
const getProdClient = () => {
  return getProdSupabaseClient();
};

/**
 * Extract tenant_id from JWT token
 * Used for direct table queries that need tenant filtering
 * (RPC functions get tenant from JWT internally)
 */
const getTenantIdFromSession = async (): Promise<string | null> => {
  try {
    const {
      data: { session }
    } = await supabase.auth.getSession();
    if (!session?.access_token) return null;

    const payload = session.access_token.split('.')[1];
    if (!payload) return null;

    const claims = JSON.parse(atob(payload));
    return claims.tenant_id || null;
  } catch {
    return null;
  }
};

/**
 * Format date to YYYY-MM-DD string
 */
export const formatRouteDate = (date: Date | string): string => {
  if (typeof date === 'string') {
    // If already a string, ensure it's in YYYY-MM-DD format
    return date.split('T')[0];
  }
  // Use local date components to avoid UTC timezone shift
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Determine initial route status based on route date
 * - Today's routes start as 'in_progress'
 * - Future routes start as 'draft'
 */
export const determineInitialRouteStatus = (routeDate: Date | string): RouteStatus => {
  const formattedRouteDate = formatRouteDate(routeDate);
  const today = formatRouteDate(new Date());
  return formattedRouteDate === today ? 'in_progress' : 'draft';
};

/**
 * Save a released recall to the route's JSONB data
 * Called before deleting an assignment when a recall is released for reassignment
 */
export async function saveReleasedRecallToRoute(
  routeId: string,
  releasedRecall: ReleasedRecall
): Promise<{ success: boolean; error: Error | null }> {
  try {
    const prodClient = getProdClient();

    // Get current route data
    const { data: route, error: fetchError } = await prodClient
      .from('technician_routes')
      .select('route_data')
      .eq('id', routeId)
      .single();

    if (fetchError) {
      throw fetchError;
    }

    if (!route) {
      return { success: false, error: new Error('Route not found') };
    }

    // Append to released_recalls array
    const routeData = route.route_data as RouteData;
    const releasedRecalls = routeData.released_recalls || [];
    releasedRecalls.push(releasedRecall);

    // Update route with new released_recalls
    const { error: updateError } = await prodClient
      .from('technician_routes')
      .update({
        route_data: { ...routeData, released_recalls: releasedRecalls },
        updated_at: new Date().toISOString()
      })
      .eq('id', routeId);

    if (updateError) {
      throw updateError;
    }

    return { success: true, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to save released recall to route');
    logSystemError(error, 'technicianRoutes.saveReleasedRecallToRoute');
    return { success: false, error };
  }
}

// =============================================================================
// Route CRUD Operations
// =============================================================================

/**
 * Get or create a route for a technician on a specific date
 */
export async function getOrCreateRoute(
  technicianId: string,
  routeDate: Date | string,
  initialRouteData?: Partial<RouteData>,
  _tenantId?: string
): Promise<{ data: TechnicianRouteRecord | null; error: Error | null }> {
  try {
    const rpcClient = getRpcClient();
    const formattedDate = formatRouteDate(routeDate);

    const rpcParams = {
      p_technician_id: technicianId,
      p_route_date: formattedDate
    };

    // eslint-disable-next-line no-console
    console.log(
      '[getOrCreateRoute] Calling get_route_by_technician_date with params:',
      JSON.stringify(rpcParams)
    );

    // First try to get existing route
    const { data: existing, error: getError } = await rpcClient.rpc(
      'get_route_by_technician_date',
      rpcParams
    );

    // eslint-disable-next-line no-console
    console.log('[getOrCreateRoute] Response:', { data: existing, error: getError });

    if (getError) {
      // eslint-disable-next-line no-console
      console.error('[getOrCreateRoute] RPC Error:', getError);
      throw getError;
    }

    // If route exists, return it
    if (existing && existing.length > 0) {
      return { data: existing[0] as TechnicianRouteRecord, error: null };
    }

    // Create new route with initial data
    const defaultRouteData: RouteData = {
      version: 1,
      start_location: {
        type: 'tenant_default',
        address: '',
        lat: 0,
        lng: 0
      },
      stops: [],
      ...initialRouteData
    };

    // Auto-determine initial status based on route date
    // Today's routes start as 'in_progress', future routes start as 'draft'
    const initialStatus = determineInitialRouteStatus(formattedDate);

    const { data: created, error: createError } = await rpcClient.rpc('upsert_technician_route', {
      p_technician_id: technicianId,
      p_route_date: formattedDate,
      p_route_data: defaultRouteData,
      p_status: initialStatus
    });

    if (createError) {
      throw createError;
    }

    return { data: (created?.[0] as TechnicianRouteRecord) ?? null, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to get or create route');
    logSystemError(error, 'technicianRoutes.getOrCreateRoute');
    return { data: null, error };
  }
}

/**
 * Update route data (stops, optimization, etc.)
 */
export async function updateRouteData(
  technicianId: string,
  routeDate: Date | string,
  routeData: RouteData,
  status?: RouteStatus,
  _tenantId?: string
): Promise<{ data: TechnicianRouteRecord | null; error: Error | null }> {
  try {
    const rpcClient = getRpcClient();
    const formattedDate = formatRouteDate(routeDate);

    // Auto-determine status if not provided based on route date
    // Today's routes start as 'in_progress', future routes start as 'draft'
    const effectiveStatus = status || determineInitialRouteStatus(formattedDate);

    const rpcParams = {
      p_technician_id: technicianId,
      p_route_date: formattedDate,
      p_route_data: routeData,
      p_status: effectiveStatus
    };

    // eslint-disable-next-line no-console
    console.log('[updateRouteData] Calling upsert_technician_route with params:', {
      ...rpcParams,
      p_route_data: '(truncated)' // Don't log full route data
    });

    const { data, error } = await rpcClient.rpc('upsert_technician_route', rpcParams);

    // eslint-disable-next-line no-console
    console.log('[updateRouteData] Response:', { data, error });

    if (error) {
      // eslint-disable-next-line no-console
      console.error('[updateRouteData] RPC Error:', error);
      throw error;
    }

    return { data: (data?.[0] as TechnicianRouteRecord) ?? null, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to update route data');
    logSystemError(error, 'technicianRoutes.updateRouteData');
    return { data: null, error };
  }
}

/**
 * Get route by technician and date (returns first/active route for backwards compatibility)
 * @deprecated Use getRoutesByTechnicianDate() for multiple routes per day support
 */
export async function getRouteByTechnicianDate(
  technicianId: string,
  routeDate: Date | string,
  _tenantId?: string
): Promise<{ data: TechnicianRouteWithDetails | null; error: Error | null }> {
  try {
    const rpcClient = getRpcClient();
    const formattedDate = formatRouteDate(routeDate);

    const rpcParams = {
      p_technician_id: technicianId,
      p_route_date: formattedDate
    };

    // eslint-disable-next-line no-console
    console.log('[getRouteByTechnicianDate] Calling RPC with params:', JSON.stringify(rpcParams));

    const { data, error } = await rpcClient.rpc('get_route_by_technician_date', rpcParams);

    // eslint-disable-next-line no-console
    console.log('[getRouteByTechnicianDate] Response:', { data, error });

    if (error) {
      // eslint-disable-next-line no-console
      console.error('[getRouteByTechnicianDate] RPC Error:', error);
      throw error;
    }

    return { data: (data?.[0] as TechnicianRouteWithDetails) ?? null, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to get route');
    logSystemError(error, 'technicianRoutes.getRouteByTechnicianDate');
    return { data: null, error };
  }
}

/**
 * Get all routes for a technician on a specific date (supports multiple routes per day)
 */
export async function getRoutesByTechnicianDate(
  technicianId: string,
  routeDate: Date | string,
  options?: {
    activeOnly?: boolean; // Filter to only active (non-completed/cancelled) routes
    tenantId?: string;
  }
): Promise<{ data: TechnicianRouteWithDetails[]; error: Error | null }> {
  try {
    const rpcClient = getRpcClient();
    const formattedDate = formatRouteDate(routeDate);

    const rpcParams = {
      p_technician_id: technicianId,
      p_route_date: formattedDate
    };

    // eslint-disable-next-line no-console
    console.log('[getRoutesByTechnicianDate] Calling RPC with params:', JSON.stringify(rpcParams));

    const { data, error } = await rpcClient.rpc('get_route_by_technician_date', rpcParams);

    // eslint-disable-next-line no-console
    console.log('[getRoutesByTechnicianDate] Response:', { data, error });

    if (error) {
      // eslint-disable-next-line no-console
      console.error('[getRoutesByTechnicianDate] RPC Error:', error);
      throw error;
    }

    let routes = (data as TechnicianRouteWithDetails[]) || [];

    // Filter to active routes if requested
    if (options?.activeOnly) {
      routes = routes.filter(r => !['completed', 'cancelled'].includes(r.status));
    }

    return { data: routes, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to get routes');
    logSystemError(error, 'technicianRoutes.getRoutesByTechnicianDate');
    return { data: [], error };
  }
}

/**
 * Get the active (non-completed, non-cancelled) route for a technician on a date
 * Returns null if no active route exists
 */
export async function getActiveRouteForDate(
  technicianId: string,
  routeDate: Date | string,
  tenantId?: string
): Promise<{ data: TechnicianRouteWithDetails | null; error: Error | null }> {
  const { data, error } = await getRoutesByTechnicianDate(technicianId, routeDate, {
    activeOnly: true,
    tenantId
  });

  if (error) {
    return { data: null, error };
  }

  // Return the highest sequence active route (should only be one)
  const activeRoute = data.length > 0 ? data[data.length - 1] : null;
  return { data: activeRoute, error: null };
}

/**
 * Get routes by date range
 */
export async function getRoutesByDateRange(
  startDate: Date | string,
  endDate: Date | string,
  options?: {
    technicianId?: string;
    status?: RouteStatus;
    tenantId?: string;
  }
): Promise<{ data: TechnicianRouteWithDetails[]; error: Error | null }> {
  try {
    const rpcClient = getRpcClient();

    const params = {
      p_start_date: formatRouteDate(startDate),
      p_end_date: formatRouteDate(endDate),
      p_technician_id: options?.technicianId || null,
      p_status: options?.status || null
    };

    // eslint-disable-next-line no-console
    console.log('[getRoutesByDateRange] Calling RPC with params:', JSON.stringify(params));

    const { data, error } = await rpcClient.rpc('get_routes_by_date_range', params);

    // eslint-disable-next-line no-console
    console.log('[getRoutesByDateRange] Response:', { dataLength: data?.length, error });

    if (error) {
      // eslint-disable-next-line no-console
      console.error('[getRoutesByDateRange] RPC Error:', error);
      throw error;
    }

    return { data: (data as TechnicianRouteWithDetails[]) || [], error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to get routes');
    logSystemError(error, 'technicianRoutes.getRoutesByDateRange');
    return { data: [], error };
  }
}

/**
 * Confirm a route (updates status and cascades to assignments)
 */
export async function confirmRoute(
  routeId: string,
  confirmedBy: string,
  _tenantId?: string
): Promise<{ success: boolean; assignmentsUpdated: number; error: Error | null }> {
  try {
    const rpcClient = getRpcClient();

    const rpcParams = {
      p_route_id: routeId,
      p_confirmed_by: confirmedBy
    };

    // eslint-disable-next-line no-console
    console.log('[confirmRoute] Calling RPC with params:', JSON.stringify(rpcParams));

    const { data, error } = await rpcClient.rpc('confirm_route', rpcParams);

    // eslint-disable-next-line no-console
    console.log('[confirmRoute] Response:', { data, error });

    if (error) {
      // eslint-disable-next-line no-console
      console.error('[confirmRoute] RPC Error:', error);
      throw error;
    }

    return {
      success: true,
      assignmentsUpdated: data?.[0]?.assignments_updated || 0,
      error: null
    };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to confirm route');
    logSystemError(error, 'technicianRoutes.confirmRoute');
    return { success: false, assignmentsUpdated: 0, error };
  }
}

/**
 * Update route status
 */
export async function updateRouteStatus(
  routeId: string,
  status: RouteStatus,
  _tenantId?: string
): Promise<{ success: boolean; error: Error | null }> {
  try {
    const rpcClient = getRpcClient();

    const rpcParams = {
      p_route_id: routeId,
      p_status: status
    };

    // eslint-disable-next-line no-console
    console.log('[updateRouteStatus] Calling RPC with params:', JSON.stringify(rpcParams));

    const { error } = await rpcClient.rpc('update_route_status', rpcParams);

    // eslint-disable-next-line no-console
    console.log('[updateRouteStatus] Response:', { error });

    if (error) {
      // eslint-disable-next-line no-console
      console.error('[updateRouteStatus] RPC Error:', error);
      throw error;
    }

    return { success: true, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to update route status');
    logSystemError(error, 'technicianRoutes.updateRouteStatus');
    return { success: false, error };
  }
}

/**
 * Delete a route by ID
 */
export async function deleteRoute(
  routeId: string,
  tenantId?: string
): Promise<{ success: boolean; error: Error | null }> {
  try {
    const prodClient = getProdSupabaseClient();

    // Get tenant_id from session if not provided
    const currentTenantId = tenantId || (await getTenantIdFromSession());

    const { error } = await prodClient
      .from('technician_routes')
      .delete()
      .eq('id', routeId)
      .eq('tenant_id', currentTenantId);

    if (error) {
      throw error;
    }

    return { success: true, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to delete route');
    logSystemError(error, 'technicianRoutes.deleteRoute');
    return { success: false, error };
  }
}

/**
 * Delete routes by technician and date (only deletes active routes, not completed/cancelled)
 * Completed routes are immutable and cannot be deleted through this function.
 */
export async function deleteRouteByTechnicianDate(
  technicianId: string,
  routeDate: Date | string,
  tenantId?: string
): Promise<{ success: boolean; error: Error | null }> {
  try {
    const prodClient = getProdSupabaseClient();
    const formattedDate = formatRouteDate(routeDate);

    // Get tenant_id from session if not provided
    const currentTenantId = tenantId || (await getTenantIdFromSession());

    // Only delete active routes (not completed or cancelled)
    // This preserves route immutability for completed routes
    const { error } = await prodClient
      .from('technician_routes')
      .delete()
      .eq('technician_id', technicianId)
      .eq('route_date', formattedDate)
      .eq('tenant_id', currentTenantId)
      .not('status', 'in', '("completed","cancelled")');

    if (error) {
      throw error;
    }

    return { success: true, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to delete route');
    logSystemError(error, 'technicianRoutes.deleteRouteByTechnicianDate');
    return { success: false, error };
  }
}

/**
 * Link assignments to a route
 */
export async function linkAssignmentsToRoute(
  routeId: string,
  assignmentIds: string[],
  _tenantId?: string
): Promise<{ updatedCount: number; error: Error | null }> {
  try {
    const rpcClient = getRpcClient();

    const { data, error } = await rpcClient.rpc('link_assignments_to_route', {
      p_route_id: routeId,
      p_assignment_ids: assignmentIds
    });

    if (error) {
      throw error;
    }

    return { updatedCount: data as number, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to link assignments to route');
    logSystemError(error, 'technicianRoutes.linkAssignmentsToRoute');
    return { updatedCount: 0, error };
  }
}

// =============================================================================
// Route Building Helpers
// =============================================================================

/**
 * Build route data from assignments
 * Groups assignments by dealer and creates stops.
 * Uses proximity-based matching (~80m) to merge stops at the same location.
 */
export function buildRouteDataFromAssignments(
  assignments: Array<{
    id: string;
    vin: string;
    campaign_number: string;
    recall_title?: string;
    dealer_id?: string;
    dealer_name?: string;
    dealer_street?: string;
    dealer_city?: string;
    dealer_region?: string;
    dealer_postal_code?: string;
    dealer_phone?: string;
    dealer_latitude?: number;
    dealer_longitude?: number;
    vehicle_year?: number;
    vehicle_make?: string;
    vehicle_model?: string;
    stock_number?: string;
    estimated_duration_minutes?: number;
    assignment_status: string;
    // Manual stop support
    stop_type?: 'recall' | 'manual';
    manual_stop_reason?: string;
    manual_stop_lat?: number;
    manual_stop_lng?: number;
    notes?: string;
    // Customer stops (copied onto assignments by a DB trigger)
    stop_kind?: 'business' | 'customer';
    contact_id?: string | null;
    address_line2?: string | null;
    dispatcher_note?: string | null;
  }>,
  startLocation?: RouteData['start_location']
): RouteData {
  // Group assignments by dealer (or by coordinates for manual stops)
  const dealerMap = new Map<
    string,
    {
      dealer_id: string;
      dealer_name: string;
      dealer_address: string;
      dealer_city: string;
      dealer_region: string;
      dealer_postal_code?: string;
      dealer_phone?: string;
      lat: number;
      lng: number;
      recalls: RouteRecall[];
      stop_type?: 'recall' | 'manual';
      isCustomer?: boolean;
      contact_id?: string | null;
      address_line2?: string | null;
    }
  >();

  /**
   * Find existing dealer key by proximity.
   * Returns the key of an existing stop if within threshold distance, or null.
   */
  const findExistingKeyByProximity = (lat: number, lng: number): string | null => {
    if (lat === 0 && lng === 0) return null;

    for (const [key, stop] of dealerMap) {
      if (stop.lat === 0 && stop.lng === 0) continue;
      // Never fold a business stop into a customer's household stop.
      if (stop.isCustomer) continue;

      const distance = calculateDistance(lat, lng, stop.lat, stop.lng);
      if (distance <= DUPLICATE_LOCATION_THRESHOLD_MILES) {
        return key;
      }
    }
    return null;
  };

  for (const assignment of assignments) {
    const isManual = assignment.stop_type === 'manual';
    // Customer stops are all named 'Customer stop': key them by household, and never merge
    // them with neighbours (or a nearby dealer) by proximity.
    const isCustomer = assignment.stop_kind === 'customer';

    // Get coordinates - prefer manual_stop coordinates for manual stops
    const lat = isManual
      ? assignment.manual_stop_lat || assignment.dealer_latitude || 0
      : assignment.dealer_latitude || 0;
    const lng = isManual
      ? assignment.manual_stop_lng || assignment.dealer_longitude || 0
      : assignment.dealer_longitude || 0;

    // First check for proximity match to existing stops (business stops only)
    let dealerKey = isCustomer ? null : findExistingKeyByProximity(lat, lng);

    // If no proximity match, use the standard key
    if (!dealerKey) {
      if (isCustomer) {
        dealerKey = `customer_${
          assignment.contact_id ||
          `${lat}_${lng}_${(assignment.address_line2 ?? '').trim().toUpperCase()}`
        }`;
      } else {
        dealerKey = isManual
          ? `manual_${lat}_${lng}`
          : assignment.dealer_id || assignment.dealer_name || 'unknown';
      }
    }

    if (!dealerMap.has(dealerKey)) {
      dealerMap.set(dealerKey, {
        dealer_id: isManual ? '' : assignment.dealer_id || '',
        dealer_name: assignment.dealer_name || (isManual ? 'Manual Stop' : 'Unknown Dealer'),
        dealer_address: assignment.dealer_street || '',
        dealer_city: assignment.dealer_city || '',
        dealer_region: assignment.dealer_region || '',
        lat,
        lng,
        recalls: [],
        ...(assignment.dealer_postal_code && { dealer_postal_code: assignment.dealer_postal_code }),
        ...(assignment.dealer_phone && !isCustomer && { dealer_phone: assignment.dealer_phone }),
        ...(isManual && { stop_type: 'manual' as const }),
        ...(isCustomer && {
          isCustomer: true,
          contact_id: assignment.contact_id ?? null,
          address_line2: assignment.address_line2 ?? null
        })
      });
    }

    const dealer = dealerMap.get(dealerKey)!;
    dealer.recalls.push({
      assignment_id: assignment.id,
      vin: assignment.vin || '', // VIN may be empty for manual stops
      campaign_number: assignment.campaign_number || '', // Empty for manual stops
      recall_title: assignment.recall_title || '',
      vehicle_year: assignment.vehicle_year ?? null,
      vehicle_make: assignment.vehicle_make || '',
      vehicle_model: assignment.vehicle_model || '',
      estimated_duration_minutes: assignment.estimated_duration_minutes || (isManual ? 30 : 60),
      status: assignment.assignment_status as RouteRecall['status'],
      ...(assignment.stock_number && { stock_number: assignment.stock_number }),
      // Include manual stop info in the recall item
      ...(isManual && { stop_type: 'manual' as const }),
      ...(assignment.manual_stop_reason && { manual_stop_reason: assignment.manual_stop_reason }),
      ...(assignment.notes && { notes: assignment.notes }),
      ...(isCustomer && {
        stop_kind: 'customer' as const,
        contact_id: assignment.contact_id ?? null,
        address_line2: assignment.address_line2 ?? null
      }),
      ...(assignment.dispatcher_note && { dispatcher_note: assignment.dispatcher_note })
    });
  }

  // Convert to stops array
  const stops: RouteStop[] = Array.from(dealerMap.values()).map((dealer, index) => ({
    stop_order: index + 1,
    dealer_id: dealer.dealer_id,
    dealer_name: dealer.dealer_name,
    dealer_address: dealer.dealer_address,
    dealer_city: dealer.dealer_city,
    dealer_region: dealer.dealer_region,
    lat: dealer.lat,
    lng: dealer.lng,
    recalls: dealer.recalls,
    recall_count: dealer.recalls.length,
    distance_from_previous_miles: 0,
    duration_from_previous_minutes: 0,
    // Include optional fields using spread syntax for exactOptionalPropertyTypes
    ...(dealer.dealer_postal_code && { dealer_postal_code: dealer.dealer_postal_code }),
    ...(dealer.dealer_phone && { dealer_phone: dealer.dealer_phone }),
    ...(dealer.stop_type && { stop_type: dealer.stop_type }),
    ...(dealer.isCustomer && {
      stop_kind: 'customer' as const,
      contact_id: dealer.contact_id ?? null,
      address_line2: dealer.address_line2 ?? null
    })
  }));

  return {
    version: 1,
    start_location: startLocation || {
      type: 'tenant_default',
      address: '',
      lat: 0,
      lng: 0
    },
    stops,
    optimization: {
      algorithm: 'manual',
      optimized_at: new Date().toISOString()
    }
  };
}

/**
 * Sync assignments to a route
 * Creates or updates the route with current assignment data
 * Applies nearest-neighbor optimization if a valid start location is provided
 */
export async function syncAssignmentsToRoute(
  technicianId: string,
  routeDate: Date | string,
  assignments: Parameters<typeof buildRouteDataFromAssignments>[0],
  startLocation?: RouteData['start_location']
): Promise<{ route: TechnicianRouteRecord | null; error: Error | null }> {
  // eslint-disable-next-line no-console
  console.log('[syncAssignmentsToRoute] Received startLocation:', startLocation);

  try {
    // Build route data from assignments
    const routeData = buildRouteDataFromAssignments(assignments, startLocation);
    // eslint-disable-next-line no-console
    console.log(
      '[syncAssignmentsToRoute] Built routeData.start_location:',
      routeData.start_location
    );

    // Apply Mapbox Optimization if we have a valid start location
    const hasValidStartLocation =
      startLocation && startLocation.lat !== 0 && startLocation.lng !== 0;

    if (hasValidStartLocation && routeData.stops.length > 1) {
      // eslint-disable-next-line no-console
      console.log(
        '[syncAssignmentsToRoute] Applying Mapbox optimization with',
        routeData.stops.length,
        'stops'
      );

      // Use Mapbox Optimization API for globally optimal route ordering
      // Falls back to nearest-neighbor on error or if too many stops
      const optimizationResult = await optimizeRouteWithMapbox(routeData.stops, {
        lat: startLocation.lat,
        lng: startLocation.lng
      });

      routeData.stops = optimizationResult.stops;
      routeData.optimization = {
        algorithm: optimizationResult.algorithm,
        optimized_at: new Date().toISOString(),
        ...(optimizationResult.totalDistanceMiles > 0 && {
          total_distance_miles: optimizationResult.totalDistanceMiles,
          total_duration_minutes: optimizationResult.totalDurationMinutes
        })
      };
    }

    // Upsert the route
    const { data: route, error: upsertError } = await updateRouteData(
      technicianId,
      routeDate,
      routeData
    );

    if (upsertError || !route) {
      throw upsertError || new Error('Failed to upsert route');
    }

    // Link assignments to the route
    const assignmentIds = assignments.map(a => a.id);
    const { error: linkError } = await linkAssignmentsToRoute(route.id, assignmentIds);

    if (linkError) {
      // Route was created but assignments aren't linked — orphan state for any
      // unlinked rows. Surface the error so callers / monitoring can react;
      // previously this was silently swallowed, hiding regressions.
      logSystemError(linkError, 'syncAssignmentsToRoute.linkStep');
      return { route, error: linkError };
    }

    return { route, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to sync assignments to route');
    logSystemError(error, 'technicianRoutes.syncAssignmentsToRoute');
    return { route: null, error };
  }
}

// =============================================================================
// Tenant Route Settings
// =============================================================================

/**
 * Get tenant route settings.
 *
 * Scoped explicitly by tenant id. It previously relied on RLS alone —
 * `.from('tenants').select('route_settings').single()` with no WHERE clause —
 * on the assumption that a user can only ever see their own tenant row.
 *
 * That assumption does not hold. `prod.tenants` carries THREE permissive SELECT
 * policies, and Postgres ORs permissive policies together. One of them is
 * `super_admin_can_read_tenants`, so for a super_admin RLS narrows to nothing:
 * all 30 tenant rows come back, `.single()` demands exactly one, and PostgREST
 * answers 406 Not Acceptable. React Query then retries three times, so every
 * route page load produced four failures in the console.
 *
 * It failed only for super_admins, which is why it went unnoticed — an ordinary
 * dispatcher gets exactly one row and the missing filter never shows.
 *
 * The general lesson, worth more than this one fix: RLS is an upper bound on
 * what a query MAY return, not a substitute for saying what it SHOULD return.
 * A query whose correctness depends on the caller's privileges breaks the
 * moment someone is granted more of them.
 */
export async function getTenantRouteSettings(): Promise<{
  data: TenantRouteSettings | null;
  error: Error | null;
}> {
  try {
    const tenantId = await getTenantIdFromSession();
    if (!tenantId) {
      // No tenant on the session yet. Absent settings are not an error — every
      // field has a default — so return empty rather than a failure the caller
      // has to special-case.
      return { data: {}, error: null };
    }

    const prodClient = getProdClient();
    const { data, error } = await prodClient
      .from('tenants')
      .select('route_settings')
      .eq('id', tenantId)
      .maybeSingle();

    if (error) {
      throw error;
    }

    const row = data as { route_settings?: TenantRouteSettings } | null;
    return { data: row?.route_settings ?? {}, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to get tenant route settings');
    logSystemError(error, 'technicianRoutes.getTenantRouteSettings');
    return { data: null, error };
  }
}

/**
 * Update tenant route settings.
 *
 * Reads the tenant from the JWT claim, like its sibling above and like the rest
 * of this file. It previously read `session.user.user_metadata.tenant_id`, which
 * is wrong on two counts:
 *
 *   1. `user_metadata` is the user-writable half of the Supabase auth profile —
 *      a client can set it. It is not a trustworthy source for a tenant scope.
 *      RLS is still the thing that actually blocks a cross-tenant write, but a
 *      predicate should not be built from a value the caller controls.
 *   2. Nothing populates it here, so the value was `undefined` and the filter
 *      became `id=eq.undefined` — matching no rows. The update reported success
 *      while writing nothing, which is consistent with `route_settings` being
 *      `{}` on all 30 tenants despite the settings UI existing.
 *
 * Now it refuses when the tenant is unknown rather than silently updating zero
 * rows, and confirms a row was actually written.
 */
export async function updateTenantRouteSettings(
  settings: TenantRouteSettings
): Promise<{ success: boolean; error: Error | null }> {
  try {
    const tenantId = await getTenantIdFromSession();
    if (!tenantId) {
      throw new Error('Unable to determine tenant association');
    }

    const prodClient = getProdClient();
    const { data, error } = await prodClient
      .from('tenants')
      .update({ route_settings: settings } as never)
      .eq('id', tenantId)
      .select('id');

    if (error) {
      throw error;
    }

    // A silent zero-row update is what made this invisible for so long.
    if (!data || data.length === 0) {
      throw new Error('Route settings were not saved');
    }

    return { success: true, error: null };
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to update tenant route settings');
    logSystemError(error, 'technicianRoutes.updateTenantRouteSettings');
    return { success: false, error };
  }
}

// =============================================================================
// Export API object
// =============================================================================

export const technicianRoutesAPI = {
  // Route CRUD
  getOrCreateRoute,
  updateRouteData,
  getRouteByTechnicianDate,
  getRoutesByTechnicianDate, // New: returns all routes for a tech/date
  getActiveRouteForDate, // New: returns only the active route
  getRoutesByDateRange,
  confirmRoute,
  updateRouteStatus,
  deleteRoute,
  deleteRouteByTechnicianDate,
  linkAssignmentsToRoute,

  // Released recalls
  saveReleasedRecallToRoute,

  // Route building
  buildRouteDataFromAssignments,
  syncAssignmentsToRoute,

  // Tenant settings
  getTenantRouteSettings,
  updateTenantRouteSettings,

  // Utilities
  formatRouteDate,
  determineInitialRouteStatus
};
